import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

export interface SolarData {
  pvYieldW:           number;
  batteryDischargeW:  number;
  batteryPercent:     number;
  gridConsumptionW:   number;
  totalConsumptionW:  number;
  batteryCapacityWh:  number;
}

export interface ThermometerReading {
  name:     string;
  deviceId: string;
  tempC:    number;
  humidity: number | null;
}

export interface TempDataPoint {
  ts:       number;
  tempC:    number;
  humidity: number | null;
}

export interface DayStat {
  date:             string;
  generationKwh:    number;
  consumptionKwh:   number;
  gridImportKwh:    number;
  gridExportKwh:    number;
  autarkiePercent:  number;
}

export interface WastePickup {
  date: string;   // ISO: YYYY-MM-DD
  type: string;   // e.g. PPK, Bio, GT, RM, A, SoH, TB
  name: string;   // German name from calendar
}

export interface SolarHistoryPoint {
  ts:               number;
  pvYieldW:         number;
  batteryDischargeW: number;
  gridConsumptionW: number;
  totalConsumptionW: number;
  batteryPercent:   number;
}

export interface UnifiDevice {
  name:       string;
  type:       string;    // uap, usw, ugw, udm, uxg
  model:      string;
  online:     boolean;
  uptime:     number;    // seconds
  ip:         string;
  numClients: number;    // -1 if not applicable
  rxBps:      number;
  txBps:      number;
}

export interface UnifiClient {
  hostname: string;
  ip:       string;
  isWifi:   boolean;
  signal:   number;    // dBm (0 for wired)
  rxBytes:  number;
  txBytes:  number;
  rxBps:    number;
  txBps:    number;
}

export interface UnifiSnapshot {
  wanUp:        boolean;
  wanRxBps:     number;
  wanTxBps:     number;
  wanUptime:    number;   // seconds
  wanLatency:   number;   // ms, -1 = unknown
  wanIp:        string | null;
  totalClients: number;
  lanClients:   number;
  wlanClients:  number;
  devices:      UnifiDevice[];
  topClients:   UnifiClient[];
}

@Injectable({ providedIn: 'root' })
export class VersionService implements OnDestroy {

  readonly newVersion$          = new Subject<string>();
  readonly solar$               = new Subject<SolarData>();
  readonly solarHistory$        = new Subject<SolarHistoryPoint[]>();
  readonly thermometers$        = new Subject<ThermometerReading[]>();
  readonly thermometerHistory$  = new Subject<{[deviceId: string]: TempDataPoint[]}>();
  readonly stats$               = new Subject<DayStat[]>();
  readonly waste$               = new Subject<WastePickup[]>();
  readonly unifi$               = new Subject<UnifiSnapshot>();

  connectedClients = 0;
  sunrise: string | null = null;
  sunset:  string | null = null;

  private knownCommit: string | null = null;
  private worker!: Worker;

  frontendCommit = '';
  backendCommit  = '';
  connected      = true;

  constructor(private zone: NgZone) {}

  connect(frontendCommit: string): void {
    this.frontendCommit = frontendCommit;

    this.worker = new Worker(new URL('./version.worker', import.meta.url), { type: 'module' });

    this.worker.onmessage = ({ data }) => {
      this.zone.run(() => {
        if      (data.kind === 'data')         this.handleMessage(data.payload);
        else if (data.kind === 'connected')    this.connected = true;
        else if (data.kind === 'disconnected') this.connected = false;
        else if (data.kind === 'error')        this.connected = false;
      });
    };
  }

  private handleMessage(msg: any): void {
    if (msg.type === 'hello') {
      this.backendCommit = msg.commit;
      if (!this.knownCommit) {
        // First hello on page load: record, never reload.
        this.knownCommit = msg.commit;
      } else if (msg.commit !== this.knownCommit) {
        // Backend restarted with a different commit → show update overlay and reload.
        this.newVersion$.next(msg.commit);
      }
    } else if (msg.type === 'update') {
      // Backend signalled it is about to restart → show update overlay immediately.
      this.newVersion$.next('');
    } else if (msg.type === 'solarHistory') {
      this.solarHistory$.next(msg.points as SolarHistoryPoint[]);
    } else if (msg.type === 'solar') {
      this.solar$.next(msg as SolarData);
    } else if (msg.type === 'thermometers') {
      this.thermometers$.next((msg as any).sensors as ThermometerReading[]);
      if ((msg as any).history) {
        this.thermometerHistory$.next((msg as any).history);
      }
    } else if (msg.type === 'stats') {
      this.stats$.next((msg as any).days as DayStat[]);
    } else if (msg.type === 'clients') {
      this.connectedClients = (msg as any).count;
    } else if (msg.type === 'sunrise') {
      this.sunrise = (msg as any).sunrise;
      this.sunset  = (msg as any).sunset;
    } else if (msg.type === 'waste') {
      this.waste$.next((msg as any).pickups as WastePickup[]);
    } else if (msg.type === 'unifi') {
      this.unifi$.next((msg as any).data as UnifiSnapshot);
    }
  }

  ngOnDestroy(): void {
    this.worker?.terminate();
  }
}
